/* ZEVIYO — shared front-end (reveal + ZEVIYO AI widget)
 * No external dependencies. Loaded with defer.
 * ZEVIYO AI answers FAQs locally. When it can't help, or the visitor asks for
 * a human directly, it offers a small in-widget form that posts to a
 * server-side endpoint (config.supportApiUrl) which emails support@zeviyo.com.
 * No API keys or secrets are ever present in this file. */
(function () {
  'use strict';
  var reduce = window.matchMedia && matchMedia('(prefers-reduced-motion:reduce)').matches;

  /* ---------------- Reveal on scroll (never leaves content hidden) --------- */
  function initReveal() {
    var els = [].slice.call(document.querySelectorAll('.zv-reveal'));
    if (!els.length) return;
    if (reduce) { els.forEach(function (e) { e.classList.add('zv-in'); }); return; }
    var ticking = false;
    function check() {
      ticking = false;
      var vh = window.innerHeight;
      els = els.filter(function (e) {
        if (e.getBoundingClientRect().top < vh * 0.92) { e.classList.add('zv-in'); return false; }
        return true;
      });
    }
    function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(check); } }
    check();
    addEventListener('scroll', onScroll, { passive: true });
    addEventListener('resize', onScroll, { passive: true });
    addEventListener('hashchange', function () { setTimeout(check, 60); });
    setTimeout(function () { els.forEach(function (e) { e.classList.add('zv-in'); }); els = []; }, 2500);
  }

  /* ---------------- FAQ accordions -------------------------------------- */
  function initAccordions(scope) {
    var root = scope || document;
    root.querySelectorAll('[data-zv-accordion] [data-zv-acc-btn]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var item = btn.closest('[data-zv-acc-item]');
        var panel = item.querySelector('[data-zv-acc-panel]');
        var open = item.hasAttribute('data-open');
        if (open) { item.removeAttribute('data-open'); btn.setAttribute('aria-expanded', 'false'); panel.style.maxHeight = '0px'; }
        else { item.setAttribute('data-open', ''); btn.setAttribute('aria-expanded', 'true'); panel.style.maxHeight = panel.scrollHeight + 'px'; }
      });
    });
    // Pre-opened items (default-open first question): set their height once laid out.
    function openInit() {
      root.querySelectorAll('[data-zv-acc-panel][data-open-init]').forEach(function (panel) {
        var item = panel.closest('[data-zv-acc-item]');
        if (item && item.hasAttribute('data-open')) { panel.style.maxHeight = panel.scrollHeight + 'px'; }
      });
    }
    requestAnimationFrame(openInit);
    // Keep any open panel sized correctly on resize (content reflow).
    var raf;
    addEventListener('resize', function () {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function () {
        root.querySelectorAll('[data-zv-acc-item][data-open] [data-zv-acc-panel]').forEach(function (panel) {
          panel.style.maxHeight = panel.scrollHeight + 'px';
        });
      });
    }, { passive: true });
  }

  /* ---------------- ZEVIYO AI widget ------------------------------------- */
  var responder = null; // future: setResponder(async (text) => "reply")

  var ESCALATE_TRIGGERS = [
    /hablar con (una persona|alguien|soporte|un humano|un agente)/,
    /quiero hablar con/,
    /necesito ayuda/,
    /\bcontactar\b/,
    /soporte humano/,
    /persona real/,
    /atenci[oó]n al cliente/,
    /\bhumano\b/
  ];

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function stripTags(html) {
    return String(html || '').replace(/<[^>]*>/g, '').trim();
  }

  function ZeviyoAI(root) {
    this.root = root;
    this.panel = root.querySelector('[data-zv-panel]');
    this.launcher = root.querySelector('[data-zv-launcher]');
    this.log = root.querySelector('[data-zv-log]');
    this.form = root.querySelector('[data-zv-form]');
    this.input = root.querySelector('[data-zv-input]');
    var cfgEl = root.querySelector('[data-zv-config]');
    try { this.cfg = cfgEl ? JSON.parse(cfgEl.textContent) : {}; } catch (e) { this.cfg = {}; }
    this.faqs = this.cfg.faqs || [];
    this.open = false;
    this.greeted = false;
    this.escalating = false;
    this.consecutiveFallbacks = 0;
    this.history = [];
    this.conversationId = 'zv-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);

    // Escalation form elements
    this.escPanel = root.querySelector('[data-zv-escalate-panel]');
    this.escName = root.querySelector('[data-zv-esc-name]');
    this.escEmail = root.querySelector('[data-zv-esc-email]');
    this.escHandle = root.querySelector('[data-zv-esc-handle]');
    this.escMessage = root.querySelector('[data-zv-esc-message]');
    this.escHoneypot = root.querySelector('[data-zv-esc-honeypot]');
    this.escSubmitBtn = root.querySelector('[data-zv-esc-submit]');
    this.escErrEmail = root.querySelector('[data-zv-esc-err-email]');
    this.escErrMessage = root.querySelector('[data-zv-esc-err-message]');
    this.escErrGeneral = root.querySelector('[data-zv-esc-err-general]');

    this.bind();
  }

  ZeviyoAI.prototype.setState = function (s) { this.root.setAttribute('data-state', s); };

  ZeviyoAI.prototype.bind = function () {
    var self = this;
    this.launcher.addEventListener('click', function () { self.toggle(); });
    var closeBtn = this.root.querySelector('[data-zv-close]');
    if (closeBtn) closeBtn.addEventListener('click', function () { self.close(); });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !self.open) return;
      if (self.escalating) { self.closeEscalate(); } else { self.close(); }
    });
    this.root.querySelectorAll('[data-zv-quick]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        var idx = chip.getAttribute('data-idx');
        var q = chip.getAttribute('data-q') || chip.textContent.trim();
        self.pushUser(q);
        self.think(function () {
          var f = (idx !== null && self.faqs[idx]) ? self.faqs[idx] : null;
          if (f) { self.consecutiveFallbacks = 0; self.pushAi(f.a); }
          else { self.offerEscalation(); }
        });
      });
    });
    if (this.form) {
      this.form.addEventListener('submit', function (e) {
        e.preventDefault();
        var text = (self.input.value || '').trim();
        if (!text) return;
        self.input.value = '';
        self.pushUser(text);
        self.answer(text);
      });
    }

    // Delegated: any element (static helper link or a bubble's inline button)
    // that requests opening the escalation form.
    this.root.addEventListener('click', function (e) {
      var t = e.target.closest && e.target.closest('[data-zv-open-escalate]');
      if (t) { self.openEscalate(); }
    });
    if (this.escPanel) {
      var back = this.escPanel.querySelector('[data-zv-escalate-back]');
      if (back) back.addEventListener('click', function () { self.closeEscalate(); });
      if (this.escSubmitBtn) this.escSubmitBtn.addEventListener('click', function () { self.submitEscalation(); });
      [this.escEmail, this.escMessage].forEach(function (field) {
        if (!field) return;
        field.addEventListener('input', function () {
          if (field === self.escEmail && self.escErrEmail) self.escErrEmail.hidden = true;
          if (field === self.escMessage && self.escErrMessage) self.escErrMessage.hidden = true;
          if (self.escErrGeneral) self.escErrGeneral.hidden = true;
        });
      });
    }
  };

  ZeviyoAI.prototype.toggle = function () { this.open ? this.close() : this.openPanel(); };

  ZeviyoAI.prototype.openPanel = function () {
    var self = this;
    this.open = true;
    this.root.setAttribute('data-open', '');
    this.launcher.setAttribute('aria-expanded', 'true');
    this.setState('open');
    if (!this.greeted) {
      this.greeted = true;
      var g = this.cfg.greeting || 'Hola, soy ZEVIYO AI. ¿En qué puedo ayudarte?';
      this.think(function () { self.pushAi(g); }, 250);
    }
    if (this.input && !this.escalating && !('ontouchstart' in window)) { setTimeout(function () { self.input.focus(); }, 260); }
  };

  ZeviyoAI.prototype.close = function () {
    this.open = false;
    this.root.removeAttribute('data-open');
    this.launcher.setAttribute('aria-expanded', 'false');
    this.setState('closed');
  };

  ZeviyoAI.prototype.push = function (who, html) {
    var b = document.createElement('div');
    b.className = 'zv-bubble zv-bubble--' + who;
    b.innerHTML = html;
    this.log.appendChild(b);
    this.log.scrollTop = this.log.scrollHeight;
    return b;
  };

  ZeviyoAI.prototype.pushUser = function (text) {
    this.history.push({ who: 'user', text: text });
    return this.push('user', text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'));
  };

  ZeviyoAI.prototype.pushAi = function (html) {
    this.history.push({ who: 'ai', text: stripTags(html) });
    return this.push('ai', html);
  };

  ZeviyoAI.prototype.think = function (done, delay) {
    var self = this;
    this.setState('loading');
    var typing = document.createElement('div');
    typing.className = 'zv-typing';
    typing.innerHTML = '<span></span><span></span><span></span>';
    this.log.appendChild(typing);
    this.log.scrollTop = this.log.scrollHeight;
    setTimeout(function () {
      typing.remove();
      self.setState('open');
      done();
    }, reduce ? 0 : (delay || 500));
  };

  ZeviyoAI.prototype.localMatch = function (text) {
    var t = text.toLowerCase();
    var best = null, score = 0;
    this.faqs.forEach(function (f) {
      var q = (f.q || '').toLowerCase();
      var s = 0;
      q.split(/\s+/).forEach(function (w) { if (w.length > 3 && t.indexOf(w) !== -1) s++; });
      if (s > score) { score = s; best = f; }
    });
    return score >= 1 ? best : null;
  };

  ZeviyoAI.prototype.isEscalationTrigger = function (text) {
    var t = text.toLowerCase();
    return ESCALATE_TRIGGERS.some(function (re) { return re.test(t); });
  };

  ZeviyoAI.prototype.contactLabel = function () {
    return this.cfg.contactLabel || 'Contactar con soporte';
  };

  ZeviyoAI.prototype.escalateButtonHtml = function () {
    return ' <button type="button" class="zv-ai__inline-btn" data-zv-open-escalate>' + this.contactLabel() + '</button>';
  };

  ZeviyoAI.prototype.offerEscalation = function (intro) {
    var text = intro || 'Todavía no tengo esa información conectada.';
    this.pushAi(text + this.escalateButtonHtml());
  };

  ZeviyoAI.prototype.answer = function (text) {
    var self = this;
    if (typeof responder === 'function') {
      this.setState('loading');
      Promise.resolve(responder(text)).then(function (reply) {
        self.setState('open'); self.pushAi(reply);
      }).catch(function () {
        self.setState('error');
        self.offerEscalation('Ha ocurrido un error.');
        self.setState('open');
      });
      return;
    }

    if (this.isEscalationTrigger(text)) {
      this.think(function () {
        self.consecutiveFallbacks = 0;
        self.pushAi('Claro, puedo ponerte en contacto con nuestro equipo de soporte humano ahora mismo.' + self.escalateButtonHtml());
      });
      return;
    }

    this.think(function () {
      var m = self.localMatch(text);
      if (m) {
        self.consecutiveFallbacks = 0;
        self.pushAi(m.a);
        return;
      }
      self.consecutiveFallbacks += 1;
      var intro = self.consecutiveFallbacks >= 2
        ? 'Veo que esto no te está resolviendo la duda. Puedo ponerte en contacto con soporte humano.'
        : 'Todavía no tengo esa información conectada.';
      self.offerEscalation(intro);
    });
  };

  ZeviyoAI.prototype.openEscalate = function () {
    if (!this.escPanel) return;
    this.escalating = true;
    this.root.setAttribute('data-escalating', '');
    if (this.escErrEmail) this.escErrEmail.hidden = true;
    if (this.escErrMessage) this.escErrMessage.hidden = true;
    if (this.escErrGeneral) this.escErrGeneral.hidden = true;
    if (this.escMessage && !this.escMessage.value) {
      var lastUser = this.history.slice().reverse().filter(function (h) { return h.who === 'user'; })[0];
      if (lastUser) this.escMessage.value = lastUser.text;
    }
    var self = this;
    if (!('ontouchstart' in window)) {
      setTimeout(function () { (self.escEmail || self.escMessage) && (self.escEmail || self.escMessage).focus(); }, 60);
    }
  };

  ZeviyoAI.prototype.closeEscalate = function () {
    this.escalating = false;
    this.root.removeAttribute('data-escalating');
  };

  ZeviyoAI.prototype.detectPlatform = function () {
    var joined = this.history.map(function (h) { return h.text; }).join(' ').toLowerCase();
    if (joined.indexOf('tiktok') !== -1) return 'TikTok';
    if (joined.indexOf('instagram') !== -1) return 'Instagram';
    return 'General';
  };

  ZeviyoAI.prototype.setEscSubmitting = function (submitting) {
    if (!this.escSubmitBtn) return;
    this.escSubmitBtn.disabled = submitting;
    this.escSubmitBtn.textContent = submitting ? 'Enviando…' : 'Enviar a soporte';
  };

  ZeviyoAI.prototype.submitEscalation = function () {
    var self = this;
    var email = (this.escEmail && this.escEmail.value || '').trim();
    var message = (this.escMessage && this.escMessage.value || '').trim();
    var emailOk = EMAIL_RE.test(email);
    var ok = true;

    if (this.escErrEmail) this.escErrEmail.hidden = emailOk;
    if (!emailOk) ok = false;
    if (this.escErrMessage) this.escErrMessage.hidden = !!message;
    if (!message) ok = false;
    if (this.escErrGeneral) this.escErrGeneral.hidden = true;
    if (!ok) return;

    var endpoint = this.cfg.supportApiUrl;
    if (!endpoint) {
      if (this.escErrGeneral) this.escErrGeneral.hidden = false;
      return;
    }

    this.setEscSubmitting(true);

    var payload = {
      name: (this.escName && this.escName.value || '').trim(),
      email: email,
      message: message,
      handle: (this.escHandle && this.escHandle.value || '').trim(),
      website: (this.escHoneypot && this.escHoneypot.value) || '',
      pageUrl: window.location.href,
      platform: this.detectPlatform(),
      conversationId: this.conversationId,
      history: this.history.slice(-20)
    };

    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (data) {
        return { status: r.status, data: data };
      });
    }).then(function (res) {
      self.setEscSubmitting(false);
      if (res.status === 200 && res.data && res.data.ok) {
        if (self.escName) self.escName.value = '';
        if (self.escEmail) self.escEmail.value = '';
        if (self.escHandle) self.escHandle.value = '';
        if (self.escMessage) self.escMessage.value = '';
        self.closeEscalate();
        self.pushAi('Mensaje enviado. Nuestro equipo te responderá por email lo antes posible.');
      } else if (self.escErrGeneral) {
        self.escErrGeneral.hidden = false;
      }
    }).catch(function () {
      self.setEscSubmitting(false);
      if (self.escErrGeneral) self.escErrGeneral.hidden = false;
    });
  };

  function initWidgets(scope) {
    (scope || document).querySelectorAll('[data-zeviyo-ai]').forEach(function (root) {
      if (root.__zvInit) return; root.__zvInit = true; new ZeviyoAI(root);
    });
  }

  window.ZeviyoAI = {
    setResponder: function (fn) { responder = fn; },
    version: '2.0'
  };

  /* ---------------- Service Explorer (categoría → servicio → paquete) ------ */
  function tabKeydown(e, tabs, i, activate) {
    var k = e.key, n = tabs.length, t = null;
    if (k === 'ArrowRight' || k === 'ArrowDown') t = tabs[(i + 1) % n];
    else if (k === 'ArrowLeft' || k === 'ArrowUp') t = tabs[(i - 1 + n) % n];
    else if (k === 'Home') t = tabs[0];
    else if (k === 'End') t = tabs[n - 1];
    if (t) { e.preventDefault(); t.focus(); activate(t); }
  }

  function ServiceExplorer(root) {
    this.root = root;
    root.classList.add('zv-exp--js');
    this.catTabs = [].slice.call(root.querySelectorAll('[data-zv-cat-tabs] [data-zv-cat]'));
    this.catPanels = [].slice.call(root.querySelectorAll('[data-zv-cat-panel]'));
    this.bind();
    if (this.catTabs.length) {
      this.selectCat(this.catTabs[0].getAttribute('data-zv-cat'));
    } else {
      // Single category (no category tabs rendered): reveal its panel(s) directly.
      var self = this;
      this.catPanels.forEach(function (p) { p.classList.add('is-active'); self.initCatPanel(p); });
    }
  }
  ServiceExplorer.prototype.bind = function () {
    var self = this;
    this.catTabs.forEach(function (t, i) {
      t.addEventListener('click', function () { self.selectCat(t.getAttribute('data-zv-cat')); });
      t.addEventListener('keydown', function (e) { tabKeydown(e, self.catTabs, i, function (nt) { self.selectCat(nt.getAttribute('data-zv-cat')); }); });
    });
  };
  ServiceExplorer.prototype.selectCat = function (catId) {
    var self = this;
    this.catTabs.forEach(function (t) {
      var on = t.getAttribute('data-zv-cat') === catId;
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
    });
    this.catPanels.forEach(function (p) {
      var on = p.getAttribute('data-zv-cat-panel') === catId;
      p.classList.toggle('is-active', on);
      if (on) self.initCatPanel(p);
    });
  };
  ServiceExplorer.prototype.initCatPanel = function (panel) {
    var svcTabs = [].slice.call(panel.querySelectorAll('[data-zv-svc-tabs] [data-zv-svc]'));
    var svcPanels = [].slice.call(panel.querySelectorAll('[data-zv-svc-panel]'));
    function selectSvc(svcId) {
      svcTabs.forEach(function (t) {
        var on = t.getAttribute('data-zv-svc') === svcId;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
      });
      svcPanels.forEach(function (p) { p.classList.toggle('is-active', p.getAttribute('data-zv-svc-panel') === svcId); });
    }
    if (!panel.__svcBound) {
      panel.__svcBound = true;
      svcTabs.forEach(function (t, i) {
        t.addEventListener('click', function () { selectSvc(t.getAttribute('data-zv-svc')); });
        t.addEventListener('keydown', function (e) { tabKeydown(e, svcTabs, i, function (nt) { selectSvc(nt.getAttribute('data-zv-svc')); }); });
      });
      panel.querySelectorAll('[data-zv-pkg-group]').forEach(function (group) {
        var pkgs = [].slice.call(group.querySelectorAll('[data-zv-pkg]'));
        pkgs.forEach(function (pk) {
          pk.addEventListener('click', function () {
            pkgs.forEach(function (x) { x.classList.remove('is-selected'); });
            pk.classList.add('is-selected');
          });
        });
      });
    }
    if (svcTabs.length) selectSvc(svcTabs[0].getAttribute('data-zv-svc'));
    else if (svcPanels.length) svcPanels[0].classList.add('is-active');
  };
  function initExplorers(scope) {
    (scope || document).querySelectorAll('[data-zv-explorer]').forEach(function (root) {
      if (root.__zvExp) return; root.__zvExp = true; new ServiceExplorer(root);
    });
  }

  /* ---------------- Flujo de compra directo, multi-servicio -------------- */
  /* Selección de packs en memoria (+ sessionStorage temporal) → al pagar,
   * se limpia el cart de Shopify, se añaden todas las líneas en una sola
   * petición atómica (/cart/add.js con items[]) y se redirige a /checkout.
   * El cliente nunca ve /cart ni el cart-drawer nativo. */
  var ZV_SESSION_KEY = 'zv_order_selections_v2';
  var ZV_LEGACY_SESSION_KEYS = ['zv_order_selections_v1'];

  (function purgeLegacyZvSession() {
    try {
      ZV_LEGACY_SESSION_KEYS.forEach(function (k) { sessionStorage.removeItem(k); });
    } catch (e) { /* sessionStorage no disponible: nada que purgar */ }
  })();

  function zvParseMoneyToCents(str) {
    var digits = String(str || '').replace(/[^\d,.]/g, '');
    digits = digits.replace(/\.(?=\d{3}(\D|$))/g, '');
    digits = digits.replace(',', '.');
    var n = parseFloat(digits);
    return Math.round((isNaN(n) ? 0 : n) * 100);
  }

  function zvFormatCents(cents, sample) {
    var amount = (cents / 100).toFixed(2).replace('.', ',');
    var symbolMatch = String(sample || '').match(/^[^\d\s]+/);
    var symbol = symbolMatch ? symbolMatch[0] : '€';
    return symbol + amount;
  }

  function ZvFlow(root) {
    this.root = root;
    this.el = root.querySelector('[data-zv-flow]');
    if (!this.el) return;
    this.cartUrl = this.el.getAttribute('data-cart-url') || '/cart/add';
    this.refKey = this.el.getAttribute('data-ref-key') || 'Referencia';
    this.emailKey = this.el.getAttribute('data-email-key') || 'Email';
    this.refInput = this.el.querySelector('[data-zv-f-ref]');
    this.emailInput = this.el.querySelector('[data-zv-f-email]');
    this.payBtn = this.el.querySelector('[data-zv-flow-pay]');
    this.payDefaultText = this.payBtn ? this.payBtn.textContent.trim() : 'PAGAR AHORA';
    this.state = { variant: '', service: '', platform: '', pack: '', level: '', price: '' };
    this.selections = [];
    this.email = '';
    this.submitting = false;
    this.open = false;
    this.loadSession();
    this.bind();
    this.renderAll();
  }

  ZvFlow.prototype.loadSession = function () {
    try {
      var raw = sessionStorage.getItem(ZV_SESSION_KEY);
      if (!raw) return;
      var data = JSON.parse(raw);
      if (data && Array.isArray(data.selections)) {
        this.selections = data.selections;
        this.email = data.email || '';
      }
    } catch (e) { /* sessionStorage no disponible o dato corrupto: se ignora */ }
  };

  ZvFlow.prototype.saveSession = function () {
    try {
      sessionStorage.setItem(ZV_SESSION_KEY, JSON.stringify({ selections: this.selections, email: this.email }));
    } catch (e) { /* no crítico: el estado en memoria sigue funcionando en esta vista */ }
  };

  ZvFlow.prototype.clearSession = function () {
    try { sessionStorage.removeItem(ZV_SESSION_KEY); } catch (e) {}
  };

  ZvFlow.prototype.bind = function () {
    var self = this;
    // Interceptar Comprar de cada pack (solo packs con producto real)
    this.root.querySelectorAll('[data-zv-buy]').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        self.openFlow(form);
      });
    });
    // CTA "Elegir plan": abre el modal con el selector de planes del servicio
    this.root.querySelectorAll('[data-zv-choose]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        self.openChooser(btn.getAttribute('data-svc'));
      });
    });
    this.el.querySelectorAll('[data-zv-plan]').forEach(function (p) {
      p.addEventListener('click', function () { self.applyPlan(p); self.hideGeneralError(); });
    });
    this.el.querySelectorAll('[data-zv-flow-close]').forEach(function (btn) {
      btn.addEventListener('click', function () { self.close(); });
    });
    var addAnother = this.el.querySelector('[data-zv-add-another]');
    if (addAnother) {
      addAnother.addEventListener('click', function () {
        if (self.addCurrentSelection()) {
          self.el.querySelectorAll('[data-zv-plan].is-selected').forEach(function (p) {
            p.classList.remove('is-selected');
            p.setAttribute('aria-pressed', 'false');
          });
          self.state.variant = ''; self.state.pack = ''; self.state.price = ''; self.state.level = '';
          if (!('ontouchstart' in window) && self.refInput) self.refInput.focus();
        }
      });
    }
    var next = this.el.querySelector('[data-zv-flow-next]');
    if (next) {
      next.addEventListener('click', function () {
        var refVal = self.refInput ? self.refInput.value.trim() : '';
        if (refVal === '' && self.selections.length > 0) { self.setStep(2); return; }
        if (self.addCurrentSelection()) { self.setStep(2); }
      });
    }
    var back = this.el.querySelector('[data-zv-flow-back]');
    if (back) back.addEventListener('click', function () { self.setStep(1); });
    if (this.payBtn) this.payBtn.addEventListener('click', function () { self.payAll(); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && self.open && document.contains(self.el)) self.close(); });
    // limpiar error al teclear
    [this.refInput, this.emailInput].forEach(function (inp) {
      if (!inp) return;
      inp.addEventListener('input', function () { self.clearError(inp); });
    });
  };

  ZvFlow.prototype.openFlow = function (form) {
    var panel = form.closest('[data-zv-svc-panel]');
    var catPanel = form.closest('[data-zv-cat-panel]');
    this.state = {
      variant: form.getAttribute('data-variant') || '',
      service: (panel && panel.getAttribute('data-svc-name')) || '',
      platform: (catPanel && catPanel.getAttribute('data-cat-name')) || '',
      pack: form.getAttribute('data-pack') || '',
      level: form.getAttribute('data-level') || '',
      price: form.getAttribute('data-price') || ''
    };
    if (this.refInput) this.refInput.value = '';
    if (this.emailInput && !this.emailInput.value) this.emailInput.value = this.email;
    this.el.hidden = false;
    this.open = true;
    document.body.classList.add('zv-flow-open');
    this.renderAll();
    this.setStep(1);
    var self = this;
    if (!('ontouchstart' in window)) { setTimeout(function () { self.refInput && self.refInput.focus(); }, 60); }
  };

  ZvFlow.prototype.openChooser = function (svcId) {
    var panel = this.root.querySelector('[data-zv-svc-panel="' + svcId + '"]');
    var catPanel = panel && panel.closest('[data-zv-cat-panel]');
    var group = this.el.querySelector('[data-zv-plans="' + svcId + '"]');
    if (!group) return;
    this.el.querySelectorAll('[data-zv-plans]').forEach(function (g) { g.hidden = true; });
    group.hidden = false;
    this.state.service = (panel && panel.getAttribute('data-svc-name')) || '';
    this.state.platform = (catPanel && catPanel.getAttribute('data-cat-name')) || '';
    var current = group.querySelector('[data-zv-plan].is-selected')
      || group.querySelector('[data-zv-plan][data-featured]')
      || group.querySelector('[data-zv-plan]');
    if (!current) return;
    this.applyPlan(current);
    if (this.refInput) this.refInput.value = '';
    if (this.emailInput && !this.emailInput.value) this.emailInput.value = this.email;
    this.el.hidden = false;
    this.open = true;
    document.body.classList.add('zv-flow-open');
    this.renderAll();
    this.setStep(1);
    var self = this;
    if (!('ontouchstart' in window)) { setTimeout(function () { self.refInput && self.refInput.focus(); }, 60); }
  };

  ZvFlow.prototype.applyPlan = function (btn) {
    var group = btn.closest('[data-zv-plans]');
    if (group) {
      group.querySelectorAll('[data-zv-plan]').forEach(function (p) {
        p.classList.remove('is-selected');
        p.setAttribute('aria-pressed', 'false');
      });
    }
    btn.classList.add('is-selected');
    btn.setAttribute('aria-pressed', 'true');
    this.state.variant = btn.getAttribute('data-variant') || '';
    this.state.pack = btn.getAttribute('data-plan') || '';
    this.state.level = btn.getAttribute('data-level') || '';
    this.state.price = btn.getAttribute('data-price') || '';
  };

  ZvFlow.prototype.close = function () {
    this.el.hidden = true;
    this.open = false;
    document.body.classList.remove('zv-flow-open');
    // Los valores de los campos se conservan para reabrir/cambiar de pack.
    // Las selecciones ya añadidas se conservan en memoria + sessionStorage.
  };

  ZvFlow.prototype.set = function (sel, text) {
    var n = this.el.querySelector(sel);
    if (n) n.textContent = text;
  };

  ZvFlow.prototype.setStep = function (n) {
    this.el.querySelectorAll('[data-zv-pane]').forEach(function (p) {
      p.hidden = p.getAttribute('data-zv-pane') !== String(n);
    });
    this.el.querySelectorAll('[data-zv-step-ind]').forEach(function (s) {
      var i = parseInt(s.getAttribute('data-zv-step-ind'), 10);
      s.classList.toggle('is-on', i === n);
      s.classList.toggle('is-done', i < n);
    });
    if (n === 2) this.renderAll();
  };

  ZvFlow.prototype.showError = function (inp, errSel) {
    if (inp) inp.classList.add('is-invalid');
    var err = this.el.querySelector(errSel);
    if (err) err.hidden = false;
  };

  ZvFlow.prototype.clearError = function (inp) {
    inp.classList.remove('is-invalid');
    var sel = inp === this.refInput ? '[data-zv-err-ref]' : '[data-zv-err-email]';
    var err = this.el.querySelector(sel);
    if (err) err.hidden = true;
  };

  ZvFlow.prototype.showGeneralError = function (msg) {
    var el = this.el.querySelector('[data-zv-err-general]');
    if (el) { if (msg) el.textContent = msg; el.hidden = false; }
  };

  ZvFlow.prototype.hideGeneralError = function () {
    var el = this.el.querySelector('[data-zv-err-general]');
    if (el) el.hidden = true;
  };

  ZvFlow.prototype.showPayError = function () {
    var el = this.el.querySelector('[data-zv-pay-err]');
    if (el) el.hidden = false;
  };

  ZvFlow.prototype.hidePayError = function () {
    var el = this.el.querySelector('[data-zv-pay-err]');
    if (el) el.hidden = true;
  };

  ZvFlow.prototype.validate = function () {
    var ok = true;
    var ref = this.refInput ? this.refInput.value.trim() : '';
    var email = this.emailInput ? this.emailInput.value.trim() : '';
    if (!ref) { this.showError(this.refInput, '[data-zv-err-ref]'); ok = false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) { this.showError(this.emailInput, '[data-zv-err-email]'); ok = false; }
    return ok;
  };

  ZvFlow.prototype.addCurrentSelection = function () {
    var ok = this.validate();
    if (!this.state.variant) { this.showGeneralError('Elige un plan antes de continuar.'); ok = false; }
    if (!ok) return false;
    var ref = this.refInput.value.trim();
    var email = this.emailInput.value.trim();
    this.email = email;
    this.selections.push({
      platform: this.state.platform,
      pack: this.state.pack,
      variant: this.state.variant,
      price: this.state.price,
      ref: ref,
      email: email
    });
    if (this.refInput) this.refInput.value = '';
    this.hideGeneralError();
    this.saveSession();
    this.renderAll();
    return true;
  };

  ZvFlow.prototype.removeSelection = function (idx) {
    this.selections.splice(idx, 1);
    this.saveSession();
    this.renderAll();
  };

  ZvFlow.prototype.editSelection = function (idx) {
    var sel = this.selections[idx];
    if (!sel) return;
    this.selections.splice(idx, 1);
    this.saveSession();
    var planBtn = this.el.querySelector('[data-zv-plan][data-variant="' + sel.variant + '"]');
    var group = planBtn && planBtn.closest('[data-zv-plans]');
    var svcId = group && group.getAttribute('data-zv-plans');
    if (svcId) this.openChooser(svcId);
    if (planBtn) this.applyPlan(planBtn);
    if (this.refInput) this.refInput.value = sel.ref;
    if (this.emailInput) this.emailInput.value = sel.email || this.email;
    this.renderAll();
    this.setStep(1);
  };

  ZvFlow.prototype.renderSelections = function (paneNum) {
    var container = this.el.querySelector('[data-zv-selections="' + paneNum + '"]');
    if (!container) return;
    container.innerHTML = '';
    var self = this;
    if (this.selections.length === 0) {
      container.hidden = true;
      return;
    }
    container.hidden = false;
    this.selections.forEach(function (sel, idx) {
      var row = document.createElement('div');
      row.className = 'zv-flow__selection';

      var info = document.createElement('div');
      info.className = 'zv-flow__selection-info';
      var plat = document.createElement('span');
      plat.className = 'zv-flow__selection-plat';
      plat.textContent = sel.platform + ' · ' + sel.pack;
      var meta = document.createElement('span');
      meta.className = 'zv-flow__selection-meta';
      meta.textContent = sel.ref;
      info.appendChild(plat);
      info.appendChild(meta);

      var side = document.createElement('div');
      side.className = 'zv-flow__selection-side';
      var price = document.createElement('span');
      price.className = 'zv-flow__selection-price';
      price.textContent = sel.price;
      var editBtn = document.createElement('button');
      editBtn.type = 'button';
      editBtn.className = 'zv-flow__selection-remove';
      editBtn.textContent = 'Editar';
      editBtn.addEventListener('click', function () { self.editSelection(idx); });
      var removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'zv-flow__selection-remove';
      removeBtn.textContent = 'Eliminar';
      removeBtn.addEventListener('click', function () { self.removeSelection(idx); });
      side.appendChild(price);
      side.appendChild(editBtn);
      side.appendChild(removeBtn);

      row.appendChild(info);
      row.appendChild(side);
      container.appendChild(row);
    });
  };

  ZvFlow.prototype.renderTotal = function () {
    var totalEl = this.el.querySelector('[data-zv-total]');
    var rowEl = this.el.querySelector('[data-zv-total-row]');
    if (!totalEl) return;
    if (this.selections.length === 0) {
      if (rowEl) rowEl.hidden = true;
      totalEl.textContent = '—';
      return;
    }
    if (rowEl) rowEl.hidden = false;
    var cents = 0;
    this.selections.forEach(function (s) { cents += zvParseMoneyToCents(s.price); });
    totalEl.textContent = zvFormatCents(cents, this.selections[0].price);
  };

  ZvFlow.prototype.updateEmptyState = function () {
    var empty = this.el.querySelector('[data-zv-empty-order]');
    if (empty) empty.hidden = this.selections.length !== 0;
  };

  ZvFlow.prototype.renderAll = function () {
    this.renderSelections('1');
    this.renderSelections('2');
    this.renderTotal();
    this.updateEmptyState();
  };

  ZvFlow.prototype.setPayState = function (isSubmitting) {
    if (!this.payBtn) return;
    this.payBtn.disabled = isSubmitting;
    this.payBtn.textContent = isSubmitting ? 'Preparando pedido…' : this.payDefaultText;
  };

  ZvFlow.prototype.payAll = function () {
    var self = this;
    if (this.submitting) return;
    var refVal = this.refInput ? this.refInput.value.trim() : '';
    if (refVal !== '') {
      if (!this.addCurrentSelection()) return;
    }
    if (this.selections.length === 0) {
      this.setStep(2);
      return;
    }
    this.submitting = true;
    this.setPayState(true);
    this.hidePayError();

    var items = this.selections.map(function (s) {
      var props = {};
      props['Plataforma'] = s.platform;
      props['Pack'] = s.pack;
      props[self.refKey] = s.ref;
      props[self.emailKey] = s.email;
      return { id: parseInt(s.variant, 10), quantity: 1, properties: props };
    });

    fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('clear_failed');
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items })
        });
      })
      .then(function (r) {
        return r.json().then(function (data) { return { ok: r.ok, data: data }; });
      })
      .then(function (res) {
        var addedCount = (res.data && res.data.items && res.data.items.length) || 0;
        if (!res.ok || res.data.status || addedCount !== items.length) {
          throw new Error('add_failed');
        }
        self.clearSession();
        window.location.href = '/checkout';
      })
      .catch(function () {
        self.submitting = false;
        self.setPayState(false);
        self.showPayError();
      });
  };

  function initFlows(scope) {
    (scope || document).querySelectorAll('[data-zv-explorer]').forEach(function (root) {
      if (root.__zvFlow) return;
      root.__zvFlow = true;
      new ZvFlow(root);
    });
  }

  function boot() { initReveal(); initAccordions(); initWidgets(); initExplorers(); initFlows(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* Theme editor: re-init a section when it is added or reloaded */
  document.addEventListener('shopify:section:load', function (e) {
    var s = e.target;
    if (!s) return;
    document.body.classList.remove('zv-flow-open');
    s.querySelectorAll('.zv-reveal').forEach(function (el) { el.classList.add('zv-in'); });
    initAccordions(s);
    initWidgets(s);
    initExplorers(s);
    initFlows(s);
  });
})();
